import React, { useCallback, useEffect, useState } from 'react';
import { api, federation } from '../../utils/api';
import { useAuth } from '../../utils/AuthContext';
import { track, PROGRAM_CREATED } from '../../utils/analytics';
import FederationGate from './FederationGate';
import {
  ErrorBanner,
  LoadingBlock,
  PageHeader,
  PROGRAM_TYPES,
  btnPrimary,
  btnSecondary,
  canWrite,
  inputClass,
  isOwner,
  isViewer,
  safeText,
} from './federationShared';

function ProgramsInner() {
  const { user } = useAuth();
  const owner = isOwner(user);
  const viewer = isViewer(user);
  const writable = canWrite(user);

  const [programs, setPrograms] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', programType: 'Other', description: '' });
  const [expandedId, setExpandedId] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [addTeamId, setAddTeamId] = useState('');
  const [membershipError, setMembershipError] = useState('');
  const [newTeamName, setNewTeamName] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      federation.getPrograms(),
      api.getTeams().catch(() => ({ teams: [] })),
    ])
      .then(([p, t]) => {
        setPrograms(p.programs || []);
        setTeams(t.teams || []);
      })
      .catch((e) => setError(e.message || 'Could not load programs'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadMemberships = async (programId) => {
    setMembershipsLoading(true);
    setMembershipError('');
    try {
      const res = await federation.getProgramTeams(programId);
      setMemberships(res.memberships || []);
    } catch (e) {
      setMemberships([]);
      setMembershipError(e.message || 'Could not load team membership');
    } finally {
      setMembershipsLoading(false);
    }
  };

  const toggleExpand = (programId) => {
    if (expandedId === programId) {
      setExpandedId(null);
      setMemberships([]);
      return;
    }
    setExpandedId(programId);
    setAddTeamId('');
    loadMemberships(programId);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!owner) {
      setError('Only organization owners can create programs');
      return;
    }
    if (!form.name.trim()) {
      setError('Program name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await federation.createProgram({
        name: form.name.trim(),
        programType: form.programType,
        description: form.description.trim() || undefined,
      });
      track(PROGRAM_CREATED, { programType: form.programType });
      setForm({ name: '', programType: 'Other', description: '' });
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message || 'Could not create program');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (program) => {
    if (!owner) {
      setError('Only organization owners can archive programs');
      return;
    }
    setError('');
    try {
      await federation.updateProgram({ id: program.id, status: 'Archived' });
      if (expandedId === program.id) setExpandedId(null);
      load();
    } catch (err) {
      setError(err.message || 'Could not archive program');
    }
  };

  const handleAddTeam = async () => {
    if (!writable || !expandedId || !addTeamId) return;
    setMembershipError('');
    try {
      await federation.addProgramTeam({ programId: expandedId, teamId: addTeamId });
      setAddTeamId('');
      await loadMemberships(expandedId);
      load();
    } catch (err) {
      setMembershipError(err.message || 'Could not add team');
    }
  };

  const handleRemoveTeam = async (membershipId) => {
    if (!writable) return;
    setMembershipError('');
    try {
      await federation.removeProgramTeam(membershipId);
      await loadMemberships(expandedId);
      load();
    } catch (err) {
      setMembershipError(err.message || 'Could not remove team');
    }
  };

  const handleCreateParticipatingTeam = async () => {
    if (!newTeamName.trim() || !expandedId) return;
    setMembershipError('');
    try {
      const result = await api.createTeam({ name: newTeamName.trim(), sport: 'soccer' });
      const teamId = result.team?.id || result.id;
      if (!teamId) throw new Error('Team ID was not returned');
      await federation.addProgramTeam({ programId: expandedId, teamId });
      setNewTeamName('');
      await loadMemberships(expandedId);
      load();
    } catch (err) {
      setMembershipError(err.message || 'Could not create participating team');
    }
  };

  const memberTeamIds = new Set(memberships.map((m) => m.teamId));
  const availableTeams = teams.filter((t) => !memberTeamIds.has(t.id));

  return (
    <>
      <PageHeader
        title="Programs"
        description="National and development pathways linked to participating teams."
        actions={
          owner && !viewer ? (
            <button type="button" className={btnPrimary} onClick={() => setShowForm((v) => !v)}>
              {showForm ? 'Cancel' : 'Create program'}
            </button>
          ) : null
        }
      />

      <ErrorBanner message={error} onRetry={load} />

      {showForm && owner && (
        <form onSubmit={handleCreate} className="border border-navy-200 rounded-xl bg-white p-5 mb-6 space-y-4 max-w-xl">
          <h2 className="text-base font-semibold text-navy-900">New program</h2>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Name</label>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              maxLength={120}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Program type</label>
            <select
              className={inputClass}
              value={form.programType}
              onChange={(e) => setForm((f) => ({ ...f, programType: e.target.value }))}
            >
              {PROGRAM_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Description</label>
            <textarea
              className={inputClass}
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              maxLength={2000}
            />
          </div>
          <button type="submit" disabled={saving} className={btnPrimary}>
            {saving ? 'Creating…' : 'Create program'}
          </button>
        </form>
      )}

      {loading && <LoadingBlock label="Loading programs…" />}

      {!loading && programs.length === 0 && !error && (
        <div className="border border-navy-200 rounded-xl bg-white px-6 py-10 text-center">
          <p className="text-navy-900 font-medium">No programs yet</p>
          <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
            Create a program, then add teams so findings and reports can be scoped across your association.
          </p>
        </div>
      )}

      {!loading && programs.length > 0 && (
        <ul className="space-y-3">
          {programs.map((program) => (
            <li key={program.id} className="border border-navy-200 rounded-xl bg-white">
              <div className="px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="font-semibold text-navy-900">{safeText(program.name)}</p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {safeText(program.programType)} · {safeText(program.status)}
                  </p>
                  {program.description ? (
                    <p className="text-sm text-navy-800 mt-2">{safeText(program.description)}</p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={btnSecondary} onClick={() => toggleExpand(program.id)}>
                    {expandedId === program.id ? 'Hide teams' : 'View teams'}
                  </button>
                  {owner && (
                    <button type="button" className={btnSecondary} onClick={() => handleArchive(program)}>
                      Archive
                    </button>
                  )}
                </div>
              </div>

              {expandedId === program.id && (
                <div className="border-t border-navy-100 px-4 py-4 space-y-3">
                  {membershipError && (
                    <p className="text-sm text-red-700" role="alert">{safeText(membershipError)}</p>
                  )}
                  {membershipsLoading ? (
                    <p className="text-sm text-gray-500">Loading membership…</p>
                  ) : memberships.length === 0 ? (
                    <p className="text-sm text-gray-500">No teams linked yet. Add teams to this program.</p>
                  ) : (
                    <ul className="divide-y divide-navy-100 border border-navy-100 rounded-lg">
                      {memberships.map((m) => (
                        <li key={m.id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <span className="text-navy-900">{safeText(m.teamName, m.teamId)}</span>
                          {writable && (
                            <button
                              type="button"
                              className="text-red-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-400 rounded"
                              onClick={() => handleRemoveTeam(m.id)}
                            >
                              Remove
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {writable && (
                    <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Add team</label>
                        <select
                          className={inputClass}
                          value={addTeamId}
                          onChange={(e) => setAddTeamId(e.target.value)}
                        >
                          <option value="">Select a team</option>
                          {availableTeams.map((t) => (
                            <option key={t.id} value={t.id}>{safeText(t.name)}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        className={btnPrimary}
                        disabled={!addTeamId}
                        onClick={handleAddTeam}
                      >
                        Add team
                      </button>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-end border-t border-navy-100 pt-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Create participating team</label>
                        <input className={inputClass} value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="Team name" maxLength={120} />
                      </div>
                      <button type="button" className={btnSecondary} disabled={!newTeamName.trim()} onClick={handleCreateParticipatingTeam}>Create and add</button>
                    </div>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function FederationPrograms() {
  return (
    <FederationGate>
      <ProgramsInner />
    </FederationGate>
  );
}
